import React, { useState, useEffect } from 'react';
import {
  Card,
  Spin,
  Alert,
  Row,
  Col,
  Statistic,
  Tag,
  Timeline,
  Button,
  Icon,
  Empty
} from 'antd';
import moment from 'moment';

function VehicleTrackingView({ shareId: propShareId }) {
  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState(null);
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [movementHistory, setMovementHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadShareData();
  }, []);

  const loadShareData = () => {
    try {
      // Get share ID from props, hash, or pathname
      let shareId = propShareId;
      
      if (!shareId) {
        const hash = window.location.hash;
        if (hash.startsWith('#track/')) {
          shareId = hash.replace('#track/', '');
        } else {
          const path = window.location.pathname;
          if (path.includes('/track/')) {
            shareId = path.split('/track/')[1]?.split('/')[0];
          }
        }
      }
      
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      // Load share data from localStorage
      const shares = JSON.parse(localStorage.getItem('vehicle_shares') || '[]');
      const share = shares.find(s => s.id === shareId);

      if (!share) {
        setError('Share link not found or expired');
        setLoading(false);
        return;
      }

      setShareData(share);
      
      // Check if date range is still valid
      const endDate = moment(share.endDate);
      const expiryDate = moment(share.endDate).add(30, 'days');
      
      if (moment().isAfter(expiryDate)) {
        setError('This share link has expired');
        setLoading(false);
        return;
      }

      // Load vehicle location and history
      // In a real implementation, this would call AssetPro API
      loadVehicleData(share);
    } catch (err) {
      console.error('Error loading share data:', err);
      setError('Failed to load tracking data');
      setLoading(false);
    }
  };

  const loadVehicleData = async (share) => {
    setLoading(true);
    try {
      // TODO: Integrate with AssetPro API to get real GPS data
      // For now, using mock data
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock current location
      setVehicleLocation({
        latitude: 19.0760 + (Math.random() - 0.5) * 0.1,
        longitude: 72.8777 + (Math.random() - 0.5) * 0.1,
        speed: Math.floor(Math.random() * 80),
        heading: Math.floor(Math.random() * 360),
        timestamp: new Date().toISOString()
      });

      // Mock movement history
      const history = generateMockHistory(share);
      setMovementHistory(history);

      setLoading(false);
    } catch (err) {
      console.error('Error loading vehicle data:', err);
      setError('Failed to load vehicle tracking data');
      setLoading(false);
    }
  };

  const generateMockHistory = (share) => {
    const history = [];
    const start = moment(share.startDate);
    const end = moment(share.endDate);
    const days = end.diff(start, 'days');
    
    // Generate mock location points
    for (let i = 0; i < Math.min(days * 10, 50); i++) {
      const time = start.clone().add(i * (days * 24 * 60 / Math.min(days * 10, 50)), 'minutes');
      if (time.isAfter(end)) break;
      
      history.push({
        id: i,
        timestamp: time.toISOString(),
        latitude: 19.0760 + (Math.random() - 0.5) * 0.2,
        longitude: 72.8777 + (Math.random() - 0.5) * 0.2,
        speed: Math.floor(Math.random() * 80),
        address: `Location ${i + 1}`
      });
    }
    
    return history.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  if (loading) {
    return (
      <div style={{ padding: '100px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading vehicle tracking data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '100px', textAlign: 'center' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  if (!shareData) {
    return (
      <div style={{ padding: '100px', textAlign: 'center' }}>
        <Empty description="No tracking data available" />
      </div>
    );
  }

  const mapUrl = vehicleLocation
    ? `https://www.google.com/maps?q=${vehicleLocation.latitude},${vehicleLocation.longitude}`
    : null;

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          icon="arrow-left"
          onClick={() => {
            window.location.href = window.location.origin;
          }}
        >
          Back to Dashboard
        </Button>
      </div>
      <Card
        title={
          <div>
            <Icon type="environment" style={{ marginRight: 8 }} />
            Vehicle Tracking: {shareData.vehicleName}
          </div>
        }
        extra={
          <Tag color="blue">
            {moment(shareData.startDate).format('DD-MMM-YYYY')} to {moment(shareData.endDate).format('DD-MMM-YYYY')}
          </Tag>
        }
      >
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Current Status"
                value={vehicleLocation ? "Active" : "Inactive"}
                prefix={<Icon type={vehicleLocation ? "check-circle" : "close-circle"} />}
                valueStyle={{ color: vehicleLocation ? '#52c41a' : '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Current Speed"
                value={vehicleLocation?.speed || 0}
                suffix="km/h"
                prefix={<Icon type="thunderbolt" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Points"
                value={movementHistory.length}
                prefix={<Icon type="dot-chart" />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Last Update"
                value={vehicleLocation ? moment(vehicleLocation.timestamp).fromNow() : 'N/A'}
                prefix={<Icon type="clock-circle" />}
              />
            </Card>
          </Col>
        </Row>

        {vehicleLocation && (
          <Card
            title="Current Location"
            style={{ marginBottom: 24 }}
            extra={
              mapUrl && (
                <Button
                  type="primary"
                  icon="environment"
                  href={mapUrl}
                  target="_blank"
                >
                  View on Map
                </Button>
              )
            }
          >
            <Row gutter={16}>
              <Col span={12}>
                <p><strong>Latitude:</strong> {vehicleLocation.latitude.toFixed(6)}</p>
                <p><strong>Longitude:</strong> {vehicleLocation.longitude.toFixed(6)}</p>
              </Col>
              <Col span={12}>
                <p><strong>Speed:</strong> {vehicleLocation.speed} km/h</p>
                <p><strong>Heading:</strong> {vehicleLocation.heading}°</p>
              </Col>
            </Row>
            {mapUrl && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <div style={{ 
                  width: '100%', 
                  height: '400px', 
                  backgroundColor: '#e6f7ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <Icon type="environment" style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
                    <p><strong>Map View</strong></p>
                    <p style={{ color: '#666' }}>Click "View on Map" to see location on Google Maps</p>
                    <p style={{ fontSize: '12px', color: '#999', marginTop: 8 }}>
                      Coordinates: {vehicleLocation.latitude.toFixed(6)}, {vehicleLocation.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        <Card title="Movement History">
          {movementHistory.length > 0 ? (
            <Timeline>
              {movementHistory.slice(-20).map((point, index) => (
                <Timeline.Item
                  key={point.id}
                  color={index === movementHistory.length - 1 ? 'green' : 'blue'}
                >
                  <div>
                    <strong>{moment(point.timestamp).format('DD-MMM-YYYY HH:mm')}</strong>
                    <div style={{ marginTop: 4 }}>
                      <Tag>{point.speed} km/h</Tag>
                      <Tag color="blue">
                        {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
                      </Tag>
                    </div>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <Empty description="No movement history available for this period" />
          )}
        </Card>
      </Card>
    </div>
  );
}

export default VehicleTrackingView;

